import { BCMSDefaultModel, BCMSRecord, BCMSStateManagerCRUD, DisabledPrompt, Link, useBCMSContext, useModel, useStateManager } from "@bcm-dev/bcms-vite";
import Util from "classes/Util";
import { PageContainer, PageContentView, WidgetContainer, WidgetView } from "components/shared";
import { InfoBanner } from "components/shared/views/ConfirmBannerView";
import { PrimaryButton, SecondaryButton } from "components/ui";
import Classes, { Account, FastBalanceMobileDevice } from "model";
import React from "react";
import { SVGAsset } from "res";
import FastBalancesAccountTable from "./components/FastBalancesAccountTable";
import FastBalancesDevicesTable from "./components/FastBalancesDevicesTable";
import FastBalancesWidget from "./components/FastBalancesWidget";
import "./styles.css";
import "./mobile.css";


/**
 * @function SettingsFastBalancesPage
 * @return {React.Component}
 * @exported true
 * @description The main component for SettingsFastBalancesPage
 */
export default function SettingsFastBalancesPage(props) {
    const { user } = useModel();
    // Initalize all model classes
    const context = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, context);
    // Sets the values for the data and ui variables
    const data = { ...getDefaultPageData(user), ...props.data ?? {} };
    const ui = { ...getDefaultPageUI(data.transfer), ...props.ui ?? {} };
    const className = props.className?(" "+props.className):"";
    // The state to be passed to the state manager
    const state = { data, ui };

    // :: React.state, React.setState -> OBJECT
    // Function to initalize the controller for the state manager
    const controller = (state, setState) => ({
        data: state.data, // Set the data property of the controller
        ui: state.ui, // Set the ui property of the controller

        // onMobileDeviceUpdate :: STRING -> * -> VOID
        // Updates the "mobile" device properties.
        onMobileDeviceUpdate: prop => val => {
            const crud = BCMSStateManagerCRUD.init(state);
            crud.update([ "data", "mobileDevice", prop], val)
            crud.submit(setState);
        },

        // onAccountUpdate :: Account, STRING -> * -> VOID
        // Updates the "account" device properties.
        onAccountUpdate: (account, prop) => val => {
            const accountsCopy = [ ...data.accounts ];
            const accountIndex = accountsCopy.findIndex(_account => _account.id === account.id);
            account.hasFastBalance = val;
            const updatedAccounts = [ 
                ...accountsCopy.slice(0, accountIndex),
                account, 
                ...accountsCopy.slice(accountIndex + 1, accountsCopy.length) 
            ];
        },

        // onEnableFastBalances :: VOID -> VOID
        // Updates the "isEnrolled" property of the current mobile device.
        onEnableFastBalances: () => {
            const defaultMobileDevice = BCMSDefaultModel.fastBalanceMobileDevice();
            let mergedMobileDeviceRecord = BCMSDefaultModel.mergeRecords(data.mobileDevice, defaultMobileDevice);
            mergedMobileDeviceRecord = { ...mergedMobileDeviceRecord, id: FastBalanceMobileDevice.nextID(), isEnrolled: true, recentlyEnrolled: true };
            const newMobileDevice = FastBalanceMobileDevice.init({ ...mergedMobileDeviceRecord });

            BCMSRecord.batch([
                FastBalanceMobileDevice.create(newMobileDevice.record)
            ], context, Classes);

            const crud = BCMSStateManagerCRUD.init(state);
            crud.update([ "data", "mobileDevice"], newMobileDevice)
            crud.submit(setState);
        }
    });

    return (
        <PageContainer className={"settings-fast-balances-page"+className} state={ state } controller={ controller }>
            <PageContent />
        </PageContainer>
    )
}

/**
 * @name getDefaultPageData
 * @returns {OBJECT}
 * @description Hook to return the default data required by the page
 */
export const getDefaultPageData = (user, mobileDevice) => {
    const mobileDevices = FastBalanceMobileDevice.initAll();
    const accounts = Account.initAll();
    user = user ?? {};

    return {
        accounts, 
        mobileDevices,
        // "George's Iphone" from the model is shown
        // by default unless another device is passed.
        mobileDevice: mobileDevice ? mobileDevice : mobileDevices[0],
        user,
    }
};

/**
 * @name getDefaultPageUI
 * @param {MakeTransfer} transfer - A MakeTransfer instance used to initalize an instance of MakeTransferUI
 * @return {OBJECT}
 * @description Hook to return the default ui required by the page
 * @note The transfer parameter is not requried, if nothing is passed than an empty instance of MakeTransfer will be used
 */
export const getDefaultPageUI = () => ({
    //control: MakeTransferUI.init(transfer ?? MakeTransfer.init())
});

function PageContent(props) {
    const { data, ui } = useStateManager();
    const mobileDevice = data.mobileDevice ?? {};

    return (
        <PageContentView>
            { mobileDevice.recentlyEnrolled && 
            <div className="capture-banner-container">
                <EnabledFastBalancesBanner />
            </div>
            }
            
            <div className="flex-container flex-vertical flex-width settings-fast-balances-container">
                {/* Show this content only in MABI23. */}
                { Util.inMabi && <FastBalancesWidget placeholder={ "" } /> }

                {/* Show this content only in ABI22 desktop and responsive. */}
                { !Util.inMabi && 
                <WidgetContainer className="fast-balances-devices">
                    <WidgetView text="Fast Balances Devices">
                        <div className="flex-container flex-horizontal info-container content-container">
                            <div className="set-width">
                                <SVGAsset id="InfoIcon" />
                            </div>
                            <div className="flex-width">
                                <p>To add a new device, you must use the mobile app.</p>
                            </div>
                        </div>
                        <div className="content-container">
                            <p>Fast Balances is currently enabled on the following devices.</p>
                        </div>
                        <FastBalancesDevicesTable mobileDevices={ data.mobileDevices } />
                    </WidgetView>
                </WidgetContainer>
                }

                <WidgetContainer className="fast-balances-account">
                    <WidgetView text="Fast Balances Accounts">
                        <div className="content-container">
                            <p>Fast Balances allows you to view balances and recent transactions right from the login screen, without entering your user ID and password.</p>
                        </div>
                        <div className="content-container">
                            <p>Check the checkbox for each account you want to include in Fast Balances.</p>
                        </div>
                        <FastBalancesAccountTable accounts={ data.accounts } />
                        <div className="flex-container flex-horizontal align-center button-container">
                            <div className="set-width">
                                <Link to="/settingsmenu">
                                    <SecondaryButton text="Cancel" dataPoint="Cancel_Button" />
                                </Link>
                            </div>
                            <div className="set-width">
                                <DisabledPrompt>
                                    <PrimaryButton text="Submit" dataPoint="Submit_Button" />
                                </DisabledPrompt>
                            </div>
                        </div>
                    </WidgetView>
                </WidgetContainer>
            </div>
        </PageContentView>
    );
}

/**
 * @function EnabledFastBalanacesBanner
 * @return {React.Component}
 * @exported false
 * @description The banner that shows when Fast Balances have been enabled
 */

function EnabledFastBalancesBanner(props){
  return(
    <InfoBanner className="enabled-fast-balances-banner info-container" dataPoint="Fast_Balances_Enabled_Banner_Close_Icon" text="Fast Balances has been enabled on this device." />
  );
}